"use client";

import { Link } from "@tanstack/react-router";
import { useId, useState } from "react";
import { toast } from "sonner";
import { ArrowRight } from "@/components/animate-ui/icons/arrow-right";
import { CircleCheckBig } from "@/components/animate-ui/icons/circle-check-big";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useFormValidation } from "@/hooks/use-form-validation";
import { apiClient } from "@/lib/api/client";
import { parseApiError } from "@/lib/api/error-parser";
import type { components } from "@/lib/api/types";
import { forgotPasswordSchema } from "@/lib/validation/auth";

export function ForgotPasswordForm() {
	const emailId = useId();
	const [email, setEmail] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isSubmitted, setIsSubmitted] = useState(false);

	// Form validation
	const validation = useFormValidation(forgotPasswordSchema);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		const formData = { email: email.trim() };

		// Validate form data
		if (!validation.validate(formData)) {
			toast.error("Please enter a valid email address");
			return;
		}

		setIsSubmitting(true);
		validation.clearErrors();

		try {
			const { error, response } = await apiClient.POST("/api/v1/auth/forgot-password", {
				body: {
					email,
				} satisfies components["schemas"]["ForgotPasswordRequest"],
			});

			if (error) {
				const parsedError = parseApiError(error, response);

				if (parsedError.fieldErrors?.length) {
					validation.setServerErrors(parsedError.fieldErrors);
				}

				throw new Error(parsedError.message || "Failed to send reset email");
			}

			setIsSubmitted(true);
			toast.success("Password reset instructions sent to your email");
		} catch (err) {
			const errorMessage = err instanceof Error ? err.message : "Failed to send reset email";
			toast.error(errorMessage);
		} finally {
			setIsSubmitting(false);
		}
	};

	if (isSubmitted) {
		return (
			<div className="flex flex-col gap-6">
				<div className="flex flex-col gap-4 text-center">
					<div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-green-600">
						<CircleCheckBig className="size-6" />
					</div>
					<div className="flex flex-col gap-2">
						<h1 className="text-2xl font-semibold tracking-tight">Check your email</h1>
						<p className="text-sm text-muted-foreground">
							We've sent a password reset link with a 6-digit code to{" "}
							<span className="font-medium text-foreground">{email}</span>
						</p>
					</div>
				</div>

				<div className="flex flex-col gap-3">
					<p className="text-center text-sm text-muted-foreground">
						Didn't receive the email? Check your spam folder or try again.
					</p>
					<Button variant="outline" onClick={() => setIsSubmitted(false)} className="w-full">
						Try again
					</Button>
				</div>

				<div className="text-center text-sm text-muted-foreground">
					<Link to="/login" className="underline-offset-4 hover:underline">
						Back to login
					</Link>
				</div>
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-6">
			<div className="flex flex-col gap-2 text-center">
				<h1 className="text-2xl font-semibold tracking-tight">Forgot password?</h1>
				<p className="text-sm text-muted-foreground">
					Enter your email and we'll send you instructions to reset your password
				</p>
			</div>

			<form onSubmit={handleSubmit} className="flex flex-col gap-6">
				<div className="flex flex-col gap-2">
					<Label htmlFor={emailId}>Email</Label>
					<Input
						id={emailId}
						type="email"
						placeholder="name@example.com"
						value={email}
						onChange={(e) => setEmail(e.target.value)}
						onBlur={() => validation.validateField("email", email, { email })}
						required
						autoComplete="email"
						disabled={isSubmitting}
						autoFocus
					/>
					{validation.getFieldError("email") && (
						<p className="text-xs text-destructive">{validation.getFieldError("email")}</p>
					)}
				</div>

				<Button type="submit" disabled={!email.trim() || isSubmitting} className="w-full">
					{isSubmitting ? (
						"Sending..."
					) : (
						<>
							Send reset instructions
							<ArrowRight className="ml-2 size-4" />
						</>
					)}
				</Button>
			</form>

			<div className="text-center text-sm">
				<Link to="/login" className="text-muted-foreground underline-offset-4 hover:underline">
					Remember your password? Sign in
				</Link>
			</div>
		</div>
	);
}
